import React, { useState } from 'react';
import {
  X,
  Search,
  Lock,
  Target,
  Database,
  Clock,
  AlertTriangle,
  Wrench,
  Activity,
  Eye,
  ChevronDown,
  ChevronUp,
  Info,
  BarChart3,
  Shield,
  Zap,
  Users,
  Server,
  Globe,
  FileText
} from 'lucide-react';

/**
 * PhaseDetailModal - Comprehensive modal for displaying detailed phase information
 * Shows techniques, tools, indicators, and other phase-specific data
 */
const PhaseDetailModal = ({
  scenario,
  phase,
  onClose,
  onVisualize
}) => {
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    indicators: true,
    tools: true,
    metadata: false
  });

  if (!scenario || !phase) return null;

  // Find the specific phase step (handle both operationalSteps and steps structures)
  const phaseStep = scenario.operationalSteps?.find(step => step.phase === phase) ||
                   scenario.steps?.find(step => step.phase === phase);

  if (!phaseStep) return null;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'CONNAITRE': return <Search className="h-6 w-6" />;
      case 'RENTRER': return <Lock className="h-6 w-6" />;
      case 'TROUVER': return <Target className="h-6 w-6" />;
      case 'EXPLOITER': return <Database className="h-6 w-6" />;
      default: return <Eye className="h-6 w-6" />;
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'CONNAITRE': return 'bg-blue-500 text-white';
      case 'RENTRER': return 'bg-orange-500 text-white';
      case 'TROUVER': return 'bg-purple-500 text-white';
      case 'EXPLOITER': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getPhaseDescription = (phase) => {
    const descriptions = {
      'CONNAITRE': 'Phase de reconnaissance et collecte d\'informations sur la cible',
      'RENTRER': 'Phase d\'intrusion initiale et établissement de l\'accès',
      'TROUVER': 'Phase de reconnaissance interne et mouvement latéral',
      'EXPLOITER': 'Phase d\'actions sur objectifs et exfiltration'
    };
    return descriptions[phase] || 'Phase du scénario d\'attaque';
  };

  const getDifficultyColor = (difficulty) => {
    // Handle both numeric (1-4) and text difficulty values
    const numericDifficulty = typeof difficulty === 'number' ? difficulty : 
      difficulty === 'Très faible' ? 1 :
      difficulty === 'Faible' ? 2 :
      difficulty === 'Moyen' ? 3 :
      difficulty === 'Élevé' ? 4 :
      difficulty === 'Très élevé' ? 4 : 2;
    
    switch (numericDifficulty) {
      case 1: return 'bg-green-100 text-green-800 border-green-200';
      case 2: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 3: return 'bg-orange-100 text-orange-800 border-orange-200';
      case 4: return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getVraisemblanceColor = (vraisemblance) => {
    switch (vraisemblance) {
      case 1: return 'bg-green-50 text-green-600'; // Faible
      case 2: return 'bg-yellow-50 text-yellow-600'; // Moyenne
      case 3: return 'bg-orange-50 text-orange-600'; // Élevée
      case 4: return 'bg-red-50 text-red-600'; // Critique
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getVraisemblanceLabel = (vraisemblance) => {
    switch (vraisemblance) {
      case 1: return 'Peu vraisemblable';
      case 2: return 'Vraisemblable';
      case 3: return 'Très vraisemblable';
      case 4: return 'Quasi certain';
      default: return 'Non définie';
    }
  };

  const getToolIcon = (tool) => {
    const toolName = (typeof tool === 'string' ? tool : tool.name || tool.tool || '').toLowerCase();
    if (toolName.includes('nmap')) return <Server className="h-4 w-4" />;
    if (toolName.includes('metasploit')) return <Zap className="h-4 w-4" />;
    if (toolName.includes('social')) return <Users className="h-4 w-4" />;
    if (toolName.includes('web') || toolName.includes('burp')) return <Globe className="h-4 w-4" />;
    if (toolName.includes('file')) return <FileText className="h-4 w-4" />;
    return <Wrench className="h-4 w-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`${getPhaseColor(phase)} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                {getPhaseIcon(phase)}
              </div>
              <div>
                <h2 className="text-xl font-bold">Phase {phase}</h2>
                <p className="text-sm opacity-90">{getPhaseDescription(phase)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onVisualize && (
                <button
                  onClick={() => onVisualize(scenario, phase)}
                  className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>Visualiser</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Technique and Description */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Technique Principale</h3>
              <button
                onClick={() => toggleSection('description')}
                className="p-1 rounded hover:bg-gray-100"
              >
                {expandedSections.description ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">{phaseStep.technique}</h4>
              {expandedSections.description && (
                <p className="text-gray-700 text-sm leading-relaxed">{phaseStep.description}</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Métadonnées</h3>
              <button
                onClick={() => toggleSection('metadata')}
                className="p-1 rounded hover:bg-gray-100"
              >
                {expandedSections.metadata ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {phaseStep.duration && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center text-blue-600 mb-1">
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Durée</span>
                  </div>
                  <div className="text-sm font-semibold text-blue-900">{phaseStep.duration}</div>
                </div>
              )}
              
              {phaseStep.difficulty && (
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="flex items-center text-orange-600 mb-1">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Difficulté</span>
                  </div>
                  <div className="text-sm font-semibold text-orange-900">
                    {typeof phaseStep.difficulty === 'number' 
                      ? `${phaseStep.difficulty}/4` 
                      : phaseStep.difficulty}
                  </div>
                </div>
              )}
              
              {phaseStep.stepProbability && (
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="flex items-center text-green-600 mb-1">
                    <BarChart3 className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Probabilité</span>
                  </div>
                  <div className="text-sm font-semibold text-green-900">
                    {phaseStep.stepProbability}/4
                  </div>
                </div>
              )}
              
              {phaseStep.vraisemblance && (
                <div className={`rounded-lg p-3 ${getVraisemblanceColor(phaseStep.vraisemblance)}`}>
                  <div className="flex items-center mb-1">
                    <Shield className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Vraisemblance</span>
                  </div>
                  <div className="text-sm font-semibold">
                    {getVraisemblanceLabel(phaseStep.vraisemblance)} ({phaseStep.vraisemblance}/4)
                  </div>
                </div>
              )}
              
              {phaseStep.stepNumber && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <div className="flex items-center text-purple-600 mb-1">
                    <Info className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Étape</span>
                  </div>
                  <div className="text-sm font-semibold text-purple-900">#{phaseStep.stepNumber}</div>
                </div>
              )}
            </div>

            {expandedSections.metadata && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Phase EBIOS RM:</span>
                    <span className="ml-2 text-gray-900">{phaseStep.phase}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Scénario:</span>
                    <span className="ml-2 text-gray-900">{scenario.name}</span>
                  </div>
                  {phaseStep.detectionDifficulty && (
                    <div>
                      <span className="font-medium text-gray-700">Difficulté de détection:</span>
                      <span className="ml-2 text-gray-900">{phaseStep.detectionDifficulty}</span>
                    </div>
                  )}
                  {scenario.attackPathReference && (
                    <div>
                      <span className="font-medium text-gray-700">Chemin d'attaque:</span>
                      <span className="ml-2 text-gray-900">{scenario.attackPathReference}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Indicators */}
          {phaseStep.indicators && phaseStep.indicators.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Indicateurs de Compromission ({phaseStep.indicators.length})
                </h3>
                <button
                  onClick={() => toggleSection('indicators')}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  {expandedSections.indicators ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
              
              {expandedSections.indicators && (
                <div className="space-y-3">
                  {phaseStep.indicators.map((indicator, index) => {
                    const indicatorData = typeof indicator === 'string' 
                      ? { name: indicator, description: null, severity: 'Medium' }
                      : {
                          name: indicator.indicator || indicator.name || `Indicateur ${index + 1}`,
                          description: indicator.description,
                          severity: indicator.severity || 'Medium'
                        };

                    return (
                      <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-medium text-yellow-900">{indicatorData.name}</h4>
                              {indicatorData.description && (
                                <p className="text-sm text-yellow-800 mt-1">{indicatorData.description}</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            indicatorData.severity === 'Critical' ? 'bg-red-100 text-red-800' :
                            indicatorData.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                            indicatorData.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {indicatorData.severity}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tools */}
          {phaseStep.tools && phaseStep.tools.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Outils Utilisés ({phaseStep.tools.length})
                </h3>
                <button
                  onClick={() => toggleSection('tools')}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  {expandedSections.tools ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>
              
              {expandedSections.tools && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {phaseStep.tools.map((tool, index) => {
                    const toolData = typeof tool === 'string'
                      ? { name: tool, description: null }
                      : {
                          name: tool.name || tool.tool || `Outil ${index + 1}`,
                          description: tool.description
                        };

                    return (
                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <div className="text-green-600 mt-0.5">
                            {getToolIcon(toolData.name)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-green-900">{toolData.name}</h4>
                            {toolData.description && (
                              <p className="text-sm text-green-800 mt-1">{toolData.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhaseDetailModal;
